import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce, timeout } from 'rxjs/operators';
import { CostCodeSummary, CostCodeFilterParams, PaginatedResult } from '#models';
import { CostCodeService } from './cost-code.service';

/** Default timeout for large CSV exports (60 seconds). */
export const EXPORT_TIMEOUT_MS = 60_000;

/** Number of records fetched per page while accumulating the full export dataset.
 *  Must not exceed the backend's GetCostCodesPaginated max page size (200). */
export const EXPORT_PAGE_SIZE = 200;

// Must match the API's ARES.Application.CostCodes.CostCodeCsvColumns exactly (in particular
// "RemediationId (UPI)" with no space before "(UPI)") so a file downloaded here can always be
// re-uploaded via Bulk Upload without any column silently failing to map by header name.
const CSV_HEADER = ['Code', 'Description', 'Company Code', 'RemediationId (UPI)'];

@Injectable({ providedIn: 'root' })
export class CostCodeExportService {
  private _costCodeService = inject(CostCodeService);

  /**
   * Fetches the full set of cost codes matching the given filters by paging through
   * the existing paginated list endpoint (page/pageSize), then builds a CSV blob
   * client-side. This mirrors the Asset Inventory export pattern and avoids relying
   * on a single, potentially slow, full-dataset server-side export call.
   */
  exportCsv(filters: CostCodeFilterParams): Observable<Blob> {
    const fetchPage = (page: number): Observable<PaginatedResult<CostCodeSummary>> =>
      this._costCodeService.getCostCodes({
        search: filters.searchTerm,
        page,
        pageSize: EXPORT_PAGE_SIZE,
      });

    return fetchPage(1).pipe(
      expand(result => (result.page < result.totalPages ? fetchPage(result.page + 1) : EMPTY)),
      reduce((all: CostCodeSummary[], result) => [...all, ...result.items], [] as CostCodeSummary[]),
      map(costCodes => this._buildCsvBlob(costCodes)),
      timeout(EXPORT_TIMEOUT_MS),
    );
  }

  downloadCsv(csvBlob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(csvBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private _buildCsvBlob(costCodes: CostCodeSummary[]): Blob {
    const rows = costCodes.map(c =>
      [c.code, c.description ?? '', c.chevronCompanyCode ?? '', c.remediationId ?? '']
        .map(value => this._escapeCsvValue(String(value)))
        .join(','),
    );
    const csvContent = [CSV_HEADER.join(','), ...rows].join('\n');
    return new Blob([csvContent], { type: 'text/csv' });
  }

  private _escapeCsvValue(value: string): string {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }
}
